import React, { useState } from 'react';
import { ShieldCheck, KeyRound, ArrowRight } from 'lucide-react';
import { useTheme } from '../ui/ThemeContext';
import { useLanguage } from '../i18n/LanguageContext';

// Gemeinsame Klassen der MFA-Schritte (gleicher Stil wie die Felder in LoginView).
export const inputClass = (isDarkMode) => `w-full px-4 py-4 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode
  ? 'bg-gray-800/30 backdrop-blur-xl border border-white/30 text-white placeholder-white/60 focus:ring-blue-400/50'
  : 'bg-white border border-slate-200 text-gray-800 placeholder-gray-400 focus:ring-[#2563EB]/40'}`;
export const primaryButtonClass = 'w-full bg-brand hover:bg-[#1D4ED8] text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed';
export const linkClass = (isDarkMode) => `text-sm font-medium ${isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-brand hover:text-[#1D4ED8]'}`;
export const labelClass = (isDarkMode) => `block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`;

// Fehlertext aus postMfa (useAuth) übersetzen; unbekannte Server-Texte unverändert.
export const mfaErrorText = (err, t) => (err.i18nKey ? t(err.i18nKey) : err.message);

export const Spinner = () => (
  <span className="w-5 h-5 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
);

/**
 * Zweiter Anmeldeschritt (SAAS-MODUS-DESIGN §5.2): 6-stelliger Code aus der Authenticator-App
 * oder ein Wiederherstellungscode. Fehler zeigt LoginView (setError); abgelaufenes Zwischen-Token -> onBack.
 */
const MfaStep = ({ mfaToken, completeMfa, setError, onBack }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return setError(t(useRecovery ? 'login.mfa.enterRecovery' : 'login.mfa.enterCode'));
    setBusy(true);
    setError('');
    try {
      await completeMfa(useRecovery ? { mfaToken, recoveryCode: value } : { mfaToken, code: value });
    } catch (err) {
      if (err.expired) return onBack(t('login.mfa.expired'));
      setError(mfaErrorText(err, t));
      setValue('');
      setBusy(false);
    }
  };

  const toggle = () => { setUseRecovery(!useRecovery); setValue(''); setError(''); };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <label htmlFor="mfa-code" className={labelClass(isDarkMode)}>
          {t(useRecovery ? 'login.mfa.recoveryLabel' : 'login.mfa.codeLabel')}
        </label>
        <input
          id="mfa-code"
          autoFocus
          disabled={busy}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`${inputClass(isDarkMode)} tracking-widest text-center text-lg`}
          {...(useRecovery
            ? { autoComplete: 'off', maxLength: 32, placeholder: 'XXXXXXXXXX' }
            : { inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 7, placeholder: '123456' })}
        />
        <p className={`text-sm mt-2 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {t(useRecovery ? 'login.mfa.recoveryHint' : 'login.mfa.codeHint')}
        </p>
      </div>

      <button type="submit" disabled={busy} className={primaryButtonClass}>
        {busy ? <Spinner /> : useRecovery ? <KeyRound className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
        <span>{t('login.mfa.confirm')}</span>
        {!busy && <ArrowRight className="w-5 h-5" />}
      </button>

      <div className="flex justify-between">
        <button type="button" onClick={toggle} disabled={busy} className={linkClass(isDarkMode)}>
          {t(useRecovery ? 'login.mfa.useApp' : 'login.mfa.useRecovery')}
        </button>
        <button type="button" onClick={() => onBack('')} disabled={busy} className={linkClass(isDarkMode)}>
          {t('login.mfa.back')}
        </button>
      </div>
    </form>
  );
};

export default MfaStep;
